import { useState } from "react";
import { CreditCard, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { Checkbox } from "@/components/ui/checkbox";
import CodePad from "@/components/vault/CodePad";
import { ACCEPT, checkFile, uploadFile } from "@/lib/upload13";
import { LABELS } from "@/components/health/Records";

function CardViewer({ hubId, base, onClose }) {
  const [err, setErr] = useState(null);
  const [imgs, setImgs] = useState(null);
  const submit = async (code) => {
    try {
      const { data: t } = await api.post(`/organizations/${hubId}/vault/unlock`, { code });
      const { data: ids } = await api.get(`${base}/insurance-card`);
      const out = {};
      for (const side of ["front", "back"]) {
        if (!ids[side]) continue;
        const r = await api.get(`/organizations/${hubId}/documents/${ids[side].doc_id}/files/${ids[side].file_id}`, { headers: { "X-Vault-Token": t.token }, responseType: "blob" });
        out[side] = { url: URL.createObjectURL(r.data), pdf: r.data.type === "application/pdf" };
      }
      setImgs(out);
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <FormDialog open onClose={onClose} title="Insurance card" testid="inscard-viewer">
      {!imgs ? <CodePad title="Enter your vault code" hint="Insurance cards are sensitive." error={err} busy={false} onSubmit={submit} testid="inscard-pad" /> : (
        <div className="space-y-3" data-testid="inscard-sides">{["front", "back"].map((s) => imgs[s] ? (
          <figure key={s}><figcaption className="text-xs font-bold text-q-muted">{s === "front" ? "Front" : "Back"}</figcaption>
            {imgs[s].pdf ? <a href={imgs[s].url} target="_blank" rel="noreferrer" className="q-btn q-btn-secondary min-h-[44px]" data-testid={`inscard-${s}`}>Open PDF</a>
              : <img src={imgs[s].url} alt={`Insurance card ${s}`} className="w-full rounded-xl object-contain" data-testid={`inscard-${s}`} />}</figure>
        ) : <p key={s} className="text-sm text-q-muted">No {s} on file.</p>)}</div>)}
    </FormDialog>
  );
}

export function Files({ hubId, base, page, locked, reload }) {
  const [view, setView] = useState(false);
  const [title, setTitle] = useState("");
  const up = async (file, path, extra) => {
    if (!file) return;
    if (checkFile(file)) { toast.error(checkFile(file)); return; }
    try { const upload_id = await uploadFile(`/organizations/${hubId}/health`, file); await api.post(`${base}/${path}`, { upload_id, ...extra }); toast.success("Filed in Documents"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pick = (testid, onFile) => <label className={`q-btn q-btn-secondary min-h-[44px] cursor-pointer ${locked ? "pointer-events-none opacity-50" : ""}`} data-testid={testid}><Upload className="h-4 w-4" />
    <input type="file" accept={ACCEPT} className="sr-only" disabled={locked} onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} /></label>;
  return (
    <section className="space-y-4" data-testid="health-files">
      <div className="flex flex-wrap items-center gap-2">
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={!page.insurance_card.front && !page.insurance_card.back} onClick={() => setView(true)} data-testid="inscard-open"><CreditCard className="h-4 w-4" /> Insurance card</button>
        <span className="flex items-center gap-1 text-xs text-q-muted">Front {pick("inscard-up-front", (f) => up(f, "insurance-card", { side: "front" }))}</span>
        <span className="flex items-center gap-1 text-xs text-q-muted">Back {pick("inscard-up-back", (f) => up(f, "insurance-card", { side: "back" }))}</span>
      </div>
      <div>
        <h3 className="mb-2 font-display text-lg font-extrabold text-q-text">Health files</h3>
        <div className="flex flex-wrap items-end gap-2"><Field label="Title"><input className="q-input min-h-[44px]" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lab results" data-testid="health-file-title" /></Field>
          {pick("health-file-up", (f) => up(f, "files", { title: title || f.name }))}</div>
        <ul className="mt-2 space-y-2">{page.files.map((d) => <li key={d.id} className="flex min-h-[44px] items-center gap-2 rounded-xl bg-q-raised p-2 text-sm" data-testid={`health-file-${d.id}`}><FileText className="h-4 w-4 text-q-muted" /><span className="min-w-0 flex-1 truncate">{d.sensitive && d.locked !== false ? "Locked document (open it in Documents)" : d.title}</span><span className="text-xs text-q-muted">{d.category}</span></li>)}</ul>
      </div>
      {view && <CardViewer hubId={hubId} base={base} onClose={() => setView(false)} />}
    </section>
  );
}

export function Sharing({ base, page, locked, reload }) {
  const kid = page.kind === "kid";
  const of = (k) => page.shares.find((s) => s.viewer === k)?.types || [];
  const save = (viewer, types) => api.put(`${base}/sharing`, { viewer, types }).then(() => { toast.success("Sharing updated"); reload(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <section data-testid="health-sharing">
      <h3 className="font-display text-lg font-extrabold text-q-text">Sharing</h3>
      <p className="mb-2 text-xs text-q-muted">{kid ? "Parents always see kids' pages. Share this page with another adult (view only)." : "Private by default. Choose what each adult can see."}</p>
      <ul className="space-y-2">{page.adults.map((a) => (
        <li key={a.key} className="q-card p-3" data-testid={`share-${a.key}`}>
          <p className="font-semibold text-q-text">{a.name}</p>
          {kid ? <label className="flex min-h-[44px] items-center gap-3 text-sm"><Checkbox className="h-6 w-6" disabled={locked || ["owner", "coparent"].includes(a.role)} checked={["owner", "coparent"].includes(a.role) || of(a.key).length > 0}
            onCheckedChange={(v) => save(a.key, v ? ["appointments"] : [])} data-testid={`share-page-${a.key}`} /> {["owner", "coparent"].includes(a.role) ? "Parent (always)" : "Can view this page"}</label> : (
            <div className="flex flex-wrap gap-x-4">{Object.entries(LABELS).map(([g, l]) => (
              <label key={g} className="flex min-h-[44px] items-center gap-2 text-sm"><Checkbox className="h-6 w-6" disabled={locked} checked={of(a.key).includes(g)}
                onCheckedChange={(v) => save(a.key, v ? [...of(a.key), g] : of(a.key).filter((x) => x !== g))} data-testid={`share-${a.key}-${g}`} /> {l}</label>))}</div>)}
        </li>))}
      </ul>
    </section>
  );
}
